const WISHLIST_API = 'https://apps-sp.webkul.com/shopify-wishlist/index.php';

export function parseWishlistJsonp(response, callback) {
  const value = response.trim().replace(/;$/, '');
  const prefix = `${callback}(`;
  if (!value.startsWith(prefix) || !value.endsWith(')')) {
    throw new Error('Unexpected wishlist response');
  }
  const payload = JSON.parse(value.slice(prefix.length, -1));
  if (typeof payload !== 'string' || payload === 'sww' || payload === 'reinstall') {
    throw new Error('Wishlist data unavailable');
  }
  return payload;
}

export function extractWishlistCategoryIds(html, defaultId) {
  const ids = new Set();
  if (Number(defaultId) > 0) ids.add(String(defaultId));
  for (const match of html.matchAll(/\/apps\/wishlist\/(\d+)/g)) ids.add(match[1]);
  return [...ids];
}

async function readWishlistResponse(params, callback) {
  const url = new URL(WISHLIST_API);
  url.search = new URLSearchParams({ p: 'front_process', callback, ...params });
  const response = await fetch(url, { cache: 'no-store', credentials: 'omit' });
  if (!response.ok) throw new Error('Wishlist request failed');
  return callback === 'account'
    ? response.json()
    : parseWishlistJsonp(await response.text(), callback);
}

async function readSavedItemCount(shop, customerId) {
  const account = await readWishlistResponse(
    { shop, customer_id: customerId },
    'account',
  );
  const categoryCount = Number(account.customer_category_count);
  if (!Number.isInteger(categoryCount) || categoryCount < 0) {
    throw new Error('Wishlist category count unavailable');
  }
  if (categoryCount === 0) return 0;

  let categoryIds = extractWishlistCategoryIds('', account.default_category_id);
  if (categoryCount > 1) {
    const categories = await readWishlistResponse(
      { shop, cust_id: customerId },
      'categoryPage',
    );
    categoryIds = extractWishlistCategoryIds(categories, account.default_category_id);
  }
  if (categoryIds.length < categoryCount) {
    throw new Error('Wishlist categories incomplete');
  }

  const savedProductIds = new Set();
  const pages = await Promise.all(categoryIds.map((folder) =>
    readWishlistResponse({ shop, cust_id: customerId, folder }, 'productPage')
  ));
  pages.forEach((html, pageIndex) => {
    const page = new DOMParser().parseFromString(html, 'text/html');
    page.querySelectorAll('.pw-wishlist-product').forEach((card, cardIndex) => {
      const productId = card.querySelector('.pw-rm-prod')?.getAttribute('data-prod');
      savedProductIds.add(productId || `${categoryIds[pageIndex]}:${cardIndex}`);
    });
  });
  return savedProductIds.size;
}

function startFavouritesCount() {
  const action = document.querySelector('.tba-auction-favourites-action');
  const shop = action?.dataset.shopDomain;
  const customerId = action?.dataset.customerId;
  if (!shop || !customerId) return;

  let requestVersion = 0;
  const refresh = async () => {
    const version = ++requestVersion;
    try {
      const count = await readSavedItemCount(shop, customerId);
      if (version !== requestVersion) return;
      document.querySelectorAll('.tba-auction-favourites-action').forEach((link) => {
        const badge = link.querySelector('.tba-auction-favourites-count');
        if (!badge) return;
        badge.textContent = String(count);
        badge.hidden = false;
        link.setAttribute('aria-label', `My auction favourites, ${count} saved ${count === 1 ? 'item' : 'items'}`);
      });
    } catch {
      if (version !== requestVersion) return;
      document.querySelectorAll('.tba-auction-favourites-count').forEach((badge) => {
        badge.hidden = true;
      });
    }
  };

  document.addEventListener('click', (event) => {
    const icon = event.target instanceof Element
      ? event.target.closest('.tba-favourite-slot .pw-icon')
      : null;
    if (!icon) return;
    const container = icon.closest('.atw-container');
    if (!container) return;
    const wasSaved = container.classList.contains('active');
    const observer = new MutationObserver(() => {
      if (container.classList.contains('active') === wasSaved) return;
      observer.disconnect();
      refresh();
    });
    observer.observe(container, { attributes: true, attributeFilter: ['class'] });
    setTimeout(() => observer.disconnect(), 10000);
  }, true);

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) refresh();
  });
  refresh();
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startFavouritesCount, { once: true });
  } else {
    startFavouritesCount();
  }
}
